import { FileText } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import {
  Deliverable,
} from '@/services/types/deliverables-types';

import { DeliverablesModal } from './DeliverablesModal';

interface DeliverablesButtonProps {
  /** List of deliverables */
  deliverables: Deliverable[];
  /** Matter ID for API calls */
  matterId?: string;
  /** Task ID for API calls */
  taskId?: string;
  /** Function to call when deliverables are updated */
  onUpdate?: () => void;
  /** Custom button text override */
  buttonText?: string;
  /** Custom button variant */
  variant?: 'default' | 'outline' | 'ghost';
  /** Custom button size */
  size?: 'sm' | 'default' | 'lg';
  /** Custom button className */
  className?: string;
  /** Whether to show the full width layout */
  fullWidth?: boolean;
  /** Whether to show detailed stats */
  showStats?: boolean;
  /** Custom modal title */
  modalTitle?: string;
  /** Custom modal description */
  modalDescription?: string;
  /** Whether to show the external link icon */
  showIcon?: boolean;
}

export const DeliverablesButton: React.FC<DeliverablesButtonProps> = ({
  deliverables,
  matterId,
  taskId,
  onUpdate,
  buttonText,
  variant = 'outline',
  size = 'default',
  className,
  fullWidth = false,
  showStats = true,
  modalTitle,
  modalDescription,
  showIcon = true,
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  // Calculate stats
  const totalCount = deliverables.length;

  const uploadedCount = deliverables.reduce(
    (sum, d) => sum + (d.matches?.length || 0),
    0,
  );

  // Default button text — show actual uploaded file count
  const defaultButtonText =
    buttonText || `${uploadedCount} File${uploadedCount !== 1 ? 's' : ''} Uploaded`;

  // Don't render if no deliverables
  if (totalCount === 0) {
    return null;
  }

  return (
    <div className="p-1">
      <Button
        variant={variant}
        size={size}
        onClick={() => setModalOpen(true)}
        className={cn(
          fullWidth && 'w-full justify-start text-left h-8 px-2 py-1',
          !fullWidth && 'justify-center h-8 px-2',
          className,
        )}
      >
        <div className="flex items-center gap-1.5 min-w-0">
          <FileText className="h-3 w-3 text-gray-500 flex-shrink-0" />
          <span className="text-xs text-gray-700 truncate">
            {defaultButtonText}
          </span>
        </div>
      </Button>

      <DeliverablesModal
        deliverables={deliverables}
        open={modalOpen}
        onOpenChange={setModalOpen}
        matterId={matterId}
        taskId={taskId}
        onUpdate={onUpdate}
        title={modalTitle}
        description={modalDescription}
      />
    </div>
  );
};
